// src/modules/app-views/products/constants.js
// *******************************************************************************
// FISCAL CONSTANTS (SHARED)
// *******************************************************************************
// Centralized fiscal constants used across products, invoices and goodsReceipt.
// Single source of truth for ICMS, PIS, COFINS, IPI, CFOP options.
// *******************************************************************************

// ─────────────────────────────────────────────────────────────────────────────
// ICMS Origin (SEFAZ orig — same for entry and exit)
// ─────────────────────────────────────────────────────────────────────────────
export const ICMS_ORIGIN_OPTIONS = [
  { value: 0, label: '0 - Nacional' },
  { value: 1, label: '1 - Estrangeira (importação direta)' },
  { value: 2, label: '2 - Estrangeira (adquirida mercado interno)' },
  { value: 3, label: '3 - Nacional (40% a 70% conteúdo importado)' },
  { value: 4, label: '4 - Nacional (processos produtivos básicos)' },
  { value: 5, label: '5 - Nacional (conteúdo importado < 40%)' },
  { value: 6, label: '6 - Estrangeira (importação direta, sem similar)' },
  { value: 7, label: '7 - Estrangeira (mercado interno, sem similar)' },
  { value: 8, label: '8 - Nacional (conteúdo importado > 70%)' },
];

// ─────────────────────────────────────────────────────────────────────────────
// ICMS CST (Regime Normal — CRT 3)
// ─────────────────────────────────────────────────────────────────────────────
export const ICMS_CST_OPTIONS = [
  { value: '00', label: '00 - Tributada integralmente' },
  { value: '10', label: '10 - Tributada com cobrança de ICMS por ST' },
  { value: '20', label: '20 - Com redução de base de cálculo' },
  { value: '30', label: '30 - Isenta/não tributada com ST' },
  { value: '40', label: '40 - Isenta' },
  { value: '41', label: '41 - Não tributada' },
  { value: '50', label: '50 - Suspensão' },
  { value: '51', label: '51 - Diferimento' },
  { value: '60', label: '60 - ICMS cobrado anteriormente por ST' },
  { value: '70', label: '70 - Redução de base de cálculo com ST' },
  { value: '90', label: '90 - Outros' },
];

// CST codes that imply ICMS-ST
export const ICMS_CST_WITH_ST = ['10', '30', '70'];

// ─────────────────────────────────────────────────────────────────────────────
// ICMS CSOSN (Simples Nacional — CRT 1/2/4)
// ─────────────────────────────────────────────────────────────────────────────
export const ICMS_CSOSN_OPTIONS = [
  { value: '101', label: '101 - Tributada com permissão de crédito' },
  { value: '102', label: '102 - Tributada sem permissão de crédito' },
  { value: '103', label: '103 - Isenção de ICMS para faixa de receita bruta' },
  { value: '201', label: '201 - Tributada com ST e crédito' },
  { value: '202', label: '202 - Tributada com ST sem crédito' },
  { value: '203', label: '203 - Isenção de ICMS com ST' },
  { value: '300', label: '300 - Imune' },
  { value: '400', label: '400 - Não tributada' },
  { value: '500', label: '500 - ICMS cobrado anteriormente por ST' },
  { value: '900', label: '900 - Outros' },
];

// CSOSN codes that imply ICMS-ST
export const ICMS_CSOSN_WITH_ST = ['201', '202', '203'];

// ─────────────────────────────────────────────────────────────────────────────
// PIS/COFINS CST (complete — entry + exit)
// ─────────────────────────────────────────────────────────────────────────────
export const PIS_COFINS_CST_OPTIONS = [
  // Exit (saída) — 01-09, 49
  { value: '01', label: '01 - Operação tributável (cumulativo)' },
  { value: '02', label: '02 - Operação tributável (não-cumulativo)' },
  { value: '04', label: '04 - Operação tributável (alíquota zero)' },
  { value: '05', label: '05 - Operação tributável (ST)' },
  { value: '06', label: '06 - Operação tributável (alíquota zero)' },
  { value: '07', label: '07 - Operação isenta da contribuição' },
  { value: '08', label: '08 - Operação sem incidência' },
  { value: '09', label: '09 - Operação com suspensão' },
  { value: '49', label: '49 - Outras operações de saída' },
  // Entry (entrada) — 50-75, 98
  { value: '50', label: '50 - Operação com direito a crédito (exclusiva de entrada)' },
  { value: '51', label: '51 - Operação com direito a crédito (importação)' },
  { value: '52', label: '52 - Operação com direito a crédito (ativos)' },
  { value: '53', label: '53 - Operação com direito a crédito (amortização/depreciação)' },
  { value: '54', label: '54 - Operação com direito a crédito (devolução)' },
  { value: '55', label: '55 - Operação com direito a crédito (outras operações)' },
  { value: '56', label: '56 - Operação com direito a crédito (operações ST)' },
  { value: '60', label: '60 - Crédito presumido (exclusiva de entrada)' },
  { value: '61', label: '61 - Crédito presumido (importação)' },
  { value: '62', label: '62 - Crédito presumido (ativos)' },
  { value: '63', label: '63 - Crédito presumido (amortização/depreciação)' },
  { value: '64', label: '64 - Crédito presumido (devolução)' },
  { value: '65', label: '65 - Crédito presumido (outras operações)' },
  { value: '66', label: '66 - Crédito presumido (operações ST)' },
  { value: '70', label: '70 - Operação de aquisição sem direito a crédito' },
  { value: '71', label: '71 - Operação de aquisição com isenção' },
  { value: '72', label: '72 - Operação de aquisição com suspensão' },
  { value: '73', label: '73 - Operação de aquisição a alíquota zero' },
  { value: '74', label: '74 - Operação de aquisição sem incidência' },
  { value: '75', label: '75 - Operação de aquisição por ST' },
  { value: '98', label: '98 - Outras operações de entrada' },
  { value: '99', label: '99 - Outras operações' },
];

// Legacy aliases for backward compatibility (products FiscalTab uses separate PIS/COFINS)
export const PIS_CST_OPTIONS = PIS_COFINS_CST_OPTIONS;
export const COFINS_CST_OPTIONS = PIS_COFINS_CST_OPTIONS;

// ─────────────────────────────────────────────────────────────────────────────
// IPI CST — Exit (saída: 50-55, 99)
// ─────────────────────────────────────────────────────────────────────────────
export const IPI_CST_EXIT_OPTIONS = [
  { value: '50', label: '50 - Saída tributada' },
  { value: '51', label: '51 - Saída tributada com alíquota zero' },
  { value: '52', label: '52 - Saída isenta' },
  { value: '53', label: '53 - Saída não tributada' },
  { value: '54', label: '54 - Saída imune' },
  { value: '55', label: '55 - Saída com suspensão' },
  { value: '99', label: '99 - Outras saídas' },
];

// Legacy alias (products FiscalTab, invoices Step2)
export const IPI_CST_OPTIONS = IPI_CST_EXIT_OPTIONS;

// ─────────────────────────────────────────────────────────────────────────────
// IPI CST — Entry (entrada: 00-05, 49)
// ─────────────────────────────────────────────────────────────────────────────
export const IPI_CST_ENTRY_OPTIONS = [
  { value: '00', label: '00 - Entrada com recuperação de crédito' },
  { value: '01', label: '01 - Entrada tributada com alíquota zero' },
  { value: '02', label: '02 - Entrada isenta' },
  { value: '03', label: '03 - Entrada não tributada' },
  { value: '04', label: '04 - Entrada imune' },
  { value: '05', label: '05 - Entrada com suspensão' },
  { value: '49', label: '49 - Outras entradas' },
];

// ─────────────────────────────────────────────────────────────────────────────
// CFOP — Exit/Sales (saída: 5xxx intra-state, 6xxx inter-state)
// ─────────────────────────────────────────────────────────────────────────────
export const CFOP_EXIT_OPTIONS = [
  { value: '5101', label: '5101 - Venda de produção do estabelecimento' },
  { value: '5102', label: '5102 - Venda de mercadoria adquirida' },
  { value: '5103', label: '5103 - Venda de produção com entrega em outro estado' },
  { value: '5104', label: '5104 - Venda de mercadoria adquirida com entrega em outro estado' },
  { value: '5401', label: '5401 - Venda de produção com ST' },
  { value: '5402', label: '5402 - Venda de mercadoria adquirida com ST' },
  { value: '5403', label: '5403 - Venda de mercadoria para consumidor final com ST' },
  { value: '5405', label: '5405 - Venda de mercadoria adquirida com ST para consumidor final' },
  { value: '5910', label: '5910 - Remessa em bonificação (Amostra grátis)' },
  { value: '5911', label: '5911 - Remessa em doação' },
  { value: '5949', label: '5949 - Outra saída de mercadoria' },
  { value: '6101', label: '6101 - Venda de produção (interestadual)' },
  { value: '6102', label: '6102 - Venda de mercadoria adquirida (interestadual)' },
  { value: '6401', label: '6401 - Venda de produção com ST (interestadual)' },
  { value: '6402', label: '6402 - Venda de mercadoria adquirida com ST (interestadual)' },
  { value: '6910', label: '6910 - Remessa em bonificação (interestadual)' },
  { value: '6911', label: '6911 - Remessa em doação (interestadual)' },
];

// Legacy alias (invoices Step2, products FiscalTab)
export const CFOP_OPTIONS = CFOP_EXIT_OPTIONS;

// ─────────────────────────────────────────────────────────────────────────────
// CFOP — Entry/Purchases (entrada: 1xxx intra, 2xxx inter, 3xxx import)
// ─────────────────────────────────────────────────────────────────────────────
export const CFOP_ENTRY_OPTIONS = [
  // Intra-state (1xxx)
  { value: '1101', label: '1101 - Compra para industrialização' },
  { value: '1102', label: '1102 - Compra para comercialização' },
  { value: '1111', label: '1111 - Compra para industrialização por encomenda' },
  { value: '1113', label: '1113 - Compra para comercialização com mercadoria sujeita a ST' },
  { value: '1116', label: '1116 - Compra para industrialização originada de encomenda futura' },
  { value: '1117', label: '1117 - Compra para comercialização originada de encomenda futura' },
  { value: '1120', label: '1120 - Compra para industrialização (drawback)' },
  { value: '1121', label: '1121 - Compra para comercialização (drawback)' },
  { value: '1126', label: '1126 - Compra para prestação de serviço sujeita ao ICMS' },
  { value: '1128', label: '1128 - Compra para prestação de serviço sujeita ao ISSQN' },
  { value: '1401', label: '1401 - Compra para industrialização com mercadoria sujeita a ST' },
  { value: '1403', label: '1403 - Compra para comercialização com mercadoria sujeita a ST' },
  { value: '1501', label: '1501 - Entrada de mercadoria para exportação' },
  { value: '1551', label: '1551 - Compra de bem para o ativo imobilizado' },
  { value: '1556', label: '1556 - Compra de material para uso ou consumo' },
  { value: '1557', label: '1557 - Transferência de material para uso ou consumo' },
  { value: '1653', label: '1653 - Compra de combustível ou lubrificante' },
  { value: '1910', label: '1910 - Entrada de bonificação, doação ou brinde' },
  { value: '1949', label: '1949 - Outra entrada de mercadoria não especificada' },
  // Inter-state (2xxx)
  { value: '2101', label: '2101 - Compra para industrialização' },
  { value: '2102', label: '2102 - Compra para comercialização' },
  { value: '2111', label: '2111 - Compra para industrialização por encomenda' },
  { value: '2113', label: '2113 - Compra para comercialização com mercadoria sujeita a ST' },
  { value: '2401', label: '2401 - Compra para industrialização com mercadoria sujeita a ST' },
  { value: '2403', label: '2403 - Compra para comercialização com mercadoria sujeita a ST' },
  { value: '2551', label: '2551 - Compra de bem para o ativo imobilizado' },
  { value: '2556', label: '2556 - Compra de material para uso ou consumo' },
  { value: '2910', label: '2910 - Entrada de bonificação, doação ou brinde' },
  { value: '2949', label: '2949 - Outra entrada de mercadoria não especificada' },
  // Import (3xxx)
  { value: '3101', label: '3101 - Compra para industrialização (importação)' },
  { value: '3102', label: '3102 - Compra para comercialização (importação)' },
  { value: '3127', label: '3127 - Compra para industrialização sob regime de drawback' },
  { value: '3551', label: '3551 - Compra de bem para o ativo imobilizado (importação)' },
  { value: '3556', label: '3556 - Compra de material para uso ou consumo (importação)' },
  { value: '3949', label: '3949 - Outra entrada de mercadoria (importação)' },
];
